import { getWeekDays, getMonths } from './date-format';
import * as React from 'react';
import DayPicker from 'react-day-picker/DayPicker';
import { DateUtils } from 'react-day-picker';
import { Handlers, HotKeys, KeyMap } from 'ui/common/hotkeys';
import { DateTimeProps } from '../date-time-props';
import { FaRegCalendarAlt } from 'react-icons/fa';
import { ButtonInputGroup } from '../../common';
import { createDateParser, DateParser } from './parse-date';
import isDate from 'lodash/isdate';
import 'react-day-picker/lib/style.css';
import { Triangle } from 'ui';
import { OnClickOutside } from 'ui/common/on-click-outside';
import styles from './date.scss';
import { ANIMATE_OUT_SPEED } from 'ui/constants';
import { getWeekStartByLocale } from 'weekstart/full';
import { keyCodeTypes } from 'ui/common/key-code-types';

interface DatePickerState {
  /**
   * Whenever the user types something, it's stored and later parsed, triggered by a separate event
   */

  usersCurrentDateInput?: string;
  /**
   * When input does not have focus, the selected date is displayed in this friendly display format
   */
  formattedForDisplay?: string;
  /**
   * When input has focus, the selected date is shown in the format users should enter dates manually
   */
  formattedForInput?: string;
  /**
   * True when datepicker calendar is open
   */
  showDatePicker?: boolean;
  /**
   * The current value
   */
  selectedDay?: Date;
  /**
   * When focused, input displays the value in the current date *input format*
   */
  inputHasFocus?: boolean;
  /**
   * True after close was triggered and before DOM removal. Used for outro animation.
   */
  isClosing?: boolean;
  /**
   *  Date displayed in calendar's caption and used while switching between months and years
   */
  month?: Date;
}

export class DatePicker extends React.Component<DateTimeProps, DatePickerState> {
  private parser: DateParser;
  private monthNames: string[];
  private weekDayNamesNarrow: string[];
  private weekDayNamesLong: string[];
  private calendarLocale: string;
  private currentFormat: string;

  private currentYear: number = new Date().getFullYear();
  private fromMonth: Date = new Date(this.currentYear - 100, 0); //supported date range 1900 - 9999
  private toMonth: Date = new Date(this.currentYear + 10, 11);

  /**
   * Creates a Date component.
   *
   */
  constructor(props: DateTimeProps) {
    super(props);

    this.parser = createDateParser(
      props.locale,
      props.createDate,
      props.parseDate,
      props.formatDate
    );

    this.calendarLocale = `${props.locale}-u-ca-gregory`;
    this.monthNames = getMonths(this.calendarLocale, 'short');
    this.weekDayNamesNarrow = getWeekDays(this.calendarLocale, 'narrow');
    this.weekDayNamesLong = getWeekDays(this.calendarLocale, 'long');
    this.currentFormat = this.parser.getInputFormatFromLocale();

    this.state = {
      formattedForDisplay: '',
      formattedForInput: '',
      selectedDay: props.value,
      showDatePicker: false,
      inputHasFocus: false,
      isClosing: false,
      month: new Date()
    };
  }

  public componentDidMount() {
    this.updateInternalState(this.props.value);
  }

  public UNSAFE_componentWillReceiveProps(props: DateTimeProps) {
    const newIsDate = isDate(props.value);
    const newValue = newIsDate && props.value.getTime();
    const oldValue = isDate(this.props.value) && this.props.value.getTime();
    if (oldValue !== newValue) {
      this.updateInternalState(props.value, {}, false);
    }
  }

  /**
   * @private Clears state to default
   *
   */
  private clearState = (additionalState: Partial<DatePickerState> = {}) => {
    this.setState(
      {
        usersCurrentDateInput: '',
        formattedForDisplay: '',
        formattedForInput: '',
        selectedDay: undefined,
        isClosing: false,
        month: undefined,
        ...additionalState
      },
      () => {
        this.props.onChange && this.props.onChange(this.state.selectedDay);
      }
    );
  };

  /**
   * @private Calculated display and input strings based on the current selected Date
   * Clears state if date argument is invalid
   *
   */
  private updateInternalState = (
    date: Date,
    additionalState: Partial<DatePickerState> = {},
    fireOnChange?: boolean
  ) => {
    if (this.parser.isValidDate(date)) {
      const { formatDate } = this.props;
      const inputFormatted = formatDate(date, {
        day: 'numeric',
        month: 'numeric',
        year: 'numeric'
      });
      this.setState({
        formattedForDisplay: formatDate(date, {
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        }),
        formattedForInput: inputFormatted,
        usersCurrentDateInput: inputFormatted,
        selectedDay: date,
        month: date,
        ...additionalState
      });
      fireOnChange && this.props.onChange && this.props.onChange(date);
    } else {
      this.clearState(additionalState);
      this.closePicker();
    }
  };

  /**
   * @private Handles blur event for date input.
   */
  private handleInputBlur = () => {
    this.setState({ inputHasFocus: false });

    // prevent firing on every focus+blur if users did not change anything
    if (this.state.usersCurrentDateInput !== this.state.formattedForInput) {

      const parsedDateInput = this.parser.parseDateInputOrUserShortcut(
        this.state.usersCurrentDateInput
      );

      this.updateInternalState(
        parsedDateInput,
        { selectedDay: parsedDateInput },
        true
      ); //TODO: true as named parameter

    }
  };

  /**
   * @private Handles parsing on pressing 'Enter' key
   *
   */
  private onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if(event.key === 'Enter') {
       this.handleInputBlur();
    }    
    this.handleInputFocus();
  };

  /**
   * @private Handles if there is any input change for date.
   *
   */
  private handleInputChange = (e: React.SyntheticEvent<any>) => {
    const { value } = e.target as HTMLInputElement;
    this.setState({ usersCurrentDateInput: value });
  };

  private handleInputFocus = () => {
    this.setState({
      inputHasFocus: true
    });
    this.closePicker();
  };

  /**
   * @private Handles selecting date from date picker
   *
   */
  private handleDayClick = (
    day: Date,
    { disabled }: any
  ) => {
    if (disabled) {
      return;
    }
    const utcDay = this.props.createDate({
      year: day.getFullYear(),
      month: day.getMonth(),
      day: day.getDate(),
      hours: 0,
      minutes: 0,
      seconds: 0,
      milliseconds: 0
    });
    this.setState({ isClosing: true });
    this.updateInternalState(utcDay, { showDatePicker: true, isClosing: true }, true);

    setTimeout(() => {
      this.setState({ isClosing: false, showDatePicker: false });
    }, ANIMATE_OUT_SPEED);

  };

  /**
   * Callback used by 3rd party component
   */
  private handleSelectedDay = (day: Date) => {
    const selectedDay = this.state.selectedDay;
    
    const utcSelectedDay = new Date(Date.UTC(selectedDay?.getUTCFullYear(), selectedDay?.getUTCMonth(), selectedDay?.getUTCDate(),
      selectedDay?.getUTCHours(), selectedDay?.getUTCMinutes(), selectedDay?.getUTCSeconds(), selectedDay?.getUTCMilliseconds()));
    // offset is positive if the local timezone is behind UTC, and negative if it is ahead
    utcSelectedDay?.setTime(utcSelectedDay.getTime() + (utcSelectedDay.getTimezoneOffset() * 60000));
    
    return DateUtils.isSameDay(utcSelectedDay, day);
  };

  /**
   * @private Handles when to hide the date picker
   *
   */
  private hidePicker = () => {
    this.closePicker();
  };

  private keyMap: KeyMap = {
    escapeKey: [keyCodeTypes.Esc]
  };

  private handlerHotkeys: Handlers = {
    escapeKey: () => this.hidePicker()
  };

  private renderDayString = (day: Date) => {
    return day.getDate().toLocaleString(this.props.locale);
  };

  private disabled = () => this.props.disablePast && DateUtils.isPastDay;

  private closePicker = () => {
    if (this.state.showDatePicker) {
      this.setState({ isClosing: true, month: this.state.selectedDay });

      setTimeout(() => {
        this.setState({
          showDatePicker: false,
          isClosing: false
        });
      }, ANIMATE_OUT_SPEED);
    }
  };

  private togglePicker = () => {
    if (this.state.showDatePicker) {
      this.closePicker();
    } else {
      this.setState({ showDatePicker: true });
    }
  };

  private get currentDisplayValue() {
    //unless user has typed something, show the date in the format user should enter dates
    // input gets cleared on blur, and only the input format and display format is kept
    let displayValueWhenInputHasFocus = this.state.usersCurrentDateInput;
    if (displayValueWhenInputHasFocus === undefined) {
      displayValueWhenInputHasFocus = this.state.formattedForInput;
    }

    return this.state.inputHasFocus
      ? displayValueWhenInputHasFocus
      : this.state.formattedForDisplay;
  }
  
  //eslint:disable-next-line
  private renderDatePickerCustomDisplay = ({ date, onChange  }) => {
    const months = this.monthNames;
    const CaptionWrapper = this.props.captionWrapper || 'div';

    const years = [];
    for (let i = this.fromMonth.getFullYear(); i <= this.toMonth.getFullYear(); i += 1) {
      years.push(i);
    }

    const handleChangeCaption = (e: any) => {
      const { name, value } = e.target;
      // if it is inside a form element
      if(e.target?.form){
        const { year, month } = e.target.form;
         return onChange(new Date(year.value, month.value));
      }

      // if the component is not renderd inside a form
      name === 'month' ? onChange(new Date(currentYear, value)) : onChange(new Date(value, currentMonth));
    };

    const currentMonth = date ? date.getMonth() : new Date().getMonth();
    const currentYear = date
      ? Number(date
        .getFullYear()
        .toLocaleString(this.props.locale)
        .replace(/[\s \, \.]/g, ''))
      : Number(new Date().getFullYear());

    return (
      <CaptionWrapper className="DayPicker-Caption">
        <span className={styles.monthCaptionWrapper}>
          <div className={`select-wrapper ${styles.monthSelectWrapper}`}>
            <select name="month"  className="form-control" onChange={handleChangeCaption} value={currentMonth}>
              {months.map((month, i) => (
                <option key={month} value={i}>
                  {month}
                </option>
              ))}
            </select>
            <div className={styles.buttonWrapper}/>
          </div>
        </span>
        
        <span className={styles.yearCaptionWrapper}>
          <div className={`select-wrapper ${styles.yearSelectWrapper}`}>
            <select name="year" className="form-control" onChange={handleChangeCaption} value={currentYear}>
              {years.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
            <div className={styles.buttonWrapper}/>
          </div>
        </span>
      </CaptionWrapper>
    );
  };

  /**
   * @private Handles changing between months and years in the calender picker.
   */
  private handleYearMonthChange = (month: Date) => {
    this.setState({ month });
  };

  private renderCaptionElement = ({ date }: any) => {    
  const Caption = this.renderDatePickerCustomDisplay;

    return (
      <Caption
          date={date}
          onChange={this.handleYearMonthChange}
        />
    );
  };

  public render() {
    const modifiers = { selected: this.handleSelectedDay };
    const { readOnly, locale } = this.props;

    if (readOnly) {
      return <div className={`input-wrapper ${styles.wrapper}`}>
        <div className="input-group form-control-padding">{this.currentDisplayValue || '\u00A0'}</div>
      </div>;
    }

    const datePicker = (
      <div className={styles.pickerWrapper}>
        <DayPicker
          locale={this.calendarLocale}
          months={this.monthNames}
          weekdaysShort={this.weekDayNamesNarrow}
          weekdaysLong={this.weekDayNamesLong}
          onDayClick={this.handleDayClick}
          modifiers={modifiers}
          disabledDays={this.disabled()}
          showOutsideDays={true}
          initialMonth={this.state.selectedDay}
          renderDay={this.renderDayString}
          month={this.state.month}
          fromMonth={this.fromMonth}
          toMonth={this.toMonth}
          onMonthChange={this.handleYearMonthChange}
          captionElement={this.renderCaptionElement}
          firstDayOfWeek={getWeekStartByLocale(locale)}
        />
      </div>
    );

    const dateIconClickHandler = !this.props.readOnly
      ? this.togglePicker
      : undefined;

    const animateOut = this.state.isClosing ? 'animate-out' : '';

    return (
      <OnClickOutside onClickOutside={this.closePicker}>
        <HotKeys keyMap={this.keyMap} handlers={this.handlerHotkeys}>
          <div
            className={`input-wrapper ${styles.wrapper}`}
          >
            <div className="input-group">
              <input
                type="text"
                className={`form-control ${styles.dateInput}`}
                placeholder={!this.props.readOnly && this.currentFormat}
                value={this.currentDisplayValue}
                onChange={this.handleInputChange}
                onFocus={this.handleInputFocus}
                readOnly={this.props.readOnly}
                disabled={this.props.readOnly}
                onBlur={this.handleInputBlur}
                autoFocus={this.props.focus}
                onKeyDown={this.onKeyDown}
              />
              <span className="input-group-btn">
                <ButtonInputGroup
                  disabled={this.props.readOnly}
                  onClick={dateIconClickHandler}
                  icon={FaRegCalendarAlt}
                />
              </span>
            </div>
          </div>
          {this.state.showDatePicker &&
            <div className={`${styles.picker} scale-animate ${animateOut}`}>
              <Triangle />
              {datePicker}
            </div>
          }
        </HotKeys>
      </OnClickOutside>
    );
  }
}
